import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { resolveSatDownloadFailure } from "@/lib/satDownloadErrors";
import { formatIsoDateMx } from "@/lib/dateMx";
import { toast } from "sonner";

const POLL_MS = 3000;
/** VerificaSolicitud SAT ~60 s × 30 intentos; no alarmar antes de ~45 min sin cambio. */
const STALE_PROGRESS_MS = 45 * 60 * 1000;

const SatDownloadJobsContext = createContext(null);

function buildCompletionNotice(job, report) {
  const start = (report?.requested_period_start || job?.fechaInicio || "").slice(0, 10);
  const end = (report?.requested_period_end || job?.fechaFin || "").slice(0, 10);
  const startMx = formatIsoDateMx(start) || start;
  const endMx = formatIsoDateMx(end) || end;
  const through = (report?.completed_through || end).slice(0, 10);
  const throughMx = formatIsoDateMx(through) || through;

  if (report?.outcome === "success") {
    return {
      variant: "success",
      title: "Proceso terminado correctamente",
      body: `Descarga SAT completada (${startMx} – ${endMx}).`,
    };
  }
  if (report?.outcome === "partial") {
    if (report?.in_progress) {
      return {
        variant: "info",
        title: "Descarga SAT en proceso",
        body: (
          <>
            Periodo solicitado: {startMx} – {endMx}. El SAT procesa la solicitud por tramos de hasta 30 días.
            La descarga continúa en segundo plano; no necesita volver a solicitarla.
          </>
        ),
      };
    }
    const pending = Array.isArray(report.pending_periods) ? report.pending_periods : [];
    const pendingText = pending.length
      ? pending
          .map((p) => {
            const a = formatIsoDateMx((p.periodo_inicio || "").slice(0, 10)) || p.periodo_inicio;
            const b = formatIsoDateMx((p.periodo_fin || "").slice(0, 10)) || p.periodo_fin;
            return a && b ? `${a} – ${b}` : a || b;
          })
          .filter(Boolean)
          .join("; ")
      : "tramos posteriores al avance guardado";
    return {
      variant: "info",
      title: "Descarga SAT en proceso",
      body: (
        <>
          Periodo solicitado: {startMx} – {endMx}. Avance hasta {throughMx}. Pendiente: {pendingText}. El sistema
          continuará automáticamente con el siguiente tramo.
        </>
      ),
    };
  }
  return null;
}

export function SatDownloadJobsProvider({ children }) {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [notices, setNotices] = useState([]);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const syncInFlightRef = useRef(false);

  const dismissNotice = useCallback((id) => {
    setNotices((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const pushNotice = useCallback((notice) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setNotices((prev) => [...prev.slice(-4), { id, ...notice }]);
    return id;
  }, []);

  const updateJob = useCallback((jobId, patch) => {
    setJobs((prev) =>
      prev.map((j) => (j.jobId === jobId ? { ...j, ...patch } : j)),
    );
  }, []);

  const removeJob = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  const upsertTrackedJob = useCallback((row, { onTerminal } = {}) => {
    const jobId = row?.job_id || row?.jobId;
    if (!jobId) return;
    const prog = row?.progress || {};
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.jobId === jobId);
      const patch = {
        jobId,
        clientId: row.client_id || row.clientId || "",
        clientLabel: row.client_label || row.client_rfc || row.clientLabel || "",
        fechaInicio: row.fecha_inicio || row.fechaInicio || "",
        fechaFin: row.fecha_fin || row.fechaFin || "",
        authLabel: row.authLabel || "SAT",
        status: "running",
        progress: {
          step: prog.step || row.status || "running",
          message: prog.message || "Descarga SAT en proceso…",
          percent: prog.percent ?? 0,
          attempt: prog.attempt ?? 0,
          maxAttempts: prog.max_attempts ?? prog.maxAttempts ?? 0,
          direccion: prog.direccion || "",
          batchIndex: prog.batch_index ?? prog.batchIndex ?? 0,
          batchTotal: prog.batch_total ?? prog.batchTotal ?? 0,
          callCount: prog.call_count ?? prog.callCount ?? 0,
        },
        statusLine: row.status_line || row.statusLine || null,
        progressStuckSince: Date.now(),
        lastProgressPercent: prog.percent ?? 0,
        staleNotified: false,
        autoAttached: !onTerminal,
        onTerminal,
      };
      if (idx >= 0) {
        const cur = prev[idx];
        const next = [...prev];
        next[idx] = {
          ...cur,
          ...patch,
          onTerminal: onTerminal || cur.onTerminal,
          staleNotified: cur.staleNotified,
          progressStuckSince: cur.progressStuckSince ?? patch.progressStuckSince,
        };
        return next;
      }
      return [...prev, patch];
    });
  }, []);

  const registerJob = useCallback(
    (payload) => {
      upsertTrackedJob(
        {
          job_id: payload.jobId,
          client_id: payload.clientId,
          client_label: payload.clientLabel,
          fecha_inicio: payload.fechaInicio,
          fecha_fin: payload.fechaFin,
          progress: payload.progress,
          status_line: payload.statusLine,
        },
        { onTerminal: payload.onTerminal },
      );
    },
    [upsertTrackedJob],
  );

  const syncActiveJobs = useCallback(async () => {
    if (!user?.id || syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    try {
      const { data } = await api.get("/sat/descarga-automatica/activas", {
        timeout: 30000,
      });
      const rows = Array.isArray(data?.jobs) ? data.jobs : [];
      for (const row of rows) {
        upsertTrackedJob(row);
      }
    } catch {
      /* sesión o red: reintento en el siguiente ciclo */
    } finally {
      syncInFlightRef.current = false;
    }
  }, [user?.id, upsertTrackedJob]);

  const pollJobOnce = useCallback(
    async (job) => {
      try {
        const { data: remote } = await api.get("/sat/descarga-automatica/estado", {
          params: { job_id: job.jobId },
          timeout: 30000,
        });
        const prog = remote.progress || {};
        const progress = {
          step: prog.step || remote.status,
          message: prog.message || "Procesando…",
          percent: prog.percent ?? 0,
          attempt: prog.attempt ?? 0,
          maxAttempts: prog.max_attempts ?? 0,
          direccion: prog.direccion || "",
          batchIndex: prog.batch_index ?? 0,
          batchTotal: prog.batch_total ?? 0,
          callCount: prog.call_count ?? 0,
        };
        const statusLine = remote.status_line || null;

        if (remote.status === "done") {
          const result = remote.result || {};
          const report =
            result.completion_report || remote.completion_report || null;
          const notice = buildCompletionNotice(job, report);
          if (notice) pushNotice(notice);
          if (notice?.variant === "success" && job.autoAttached) {
            toast.success(notice.title, {
              description:
                typeof notice.body === "string"
                  ? notice.body
                  : "La descarga SAT finalizó correctamente.",
            });
          }
          updateJob(job.jobId, {
            status: report?.outcome === "partial" ? "partial" : "done",
            progress: {
              step: "done",
              message: result.message || remote.progress?.message || "Completado",
              percent: 100,
            },
            result,
            completionReport: report,
          });
          job.onTerminal?.({ status: "done", job: remote, report });
          setTimeout(() => removeJob(job.jobId), 8000);
          return true;
        }

        if (remote.status === "running" && remote.error) {
          const failure = resolveSatDownloadFailure({
            job: remote,
            response: { data: { detail: remote.error } },
          });
          updateJob(job.jobId, {
            status: "error",
            progress: {
              step: "error",
              message: failure.message || failure.detail || "Error en la descarga",
              percent: 100,
            },
            error: remote.error,
          });
          job.onTerminal?.({ status: "error", job: remote, failure });
          setTimeout(() => removeJob(job.jobId), 12000);
          return true;
        }

        if (remote.status === "error" || remote.status === "failed") {
          const failure = resolveSatDownloadFailure({
            job: remote,
            response: { data: { detail: remote.error } },
          });
          updateJob(job.jobId, {
            status: "error",
            progress: {
              step: "error",
              message:
                failure.message ||
                failure.technicalSummary ||
                remote.error?.message ||
                "Error en la descarga",
              percent: 100,
            },
            error: remote.error,
          });
          job.onTerminal?.({ status: "error", job: remote, failure });
          setTimeout(() => removeJob(job.jobId), 12000);
          return true;
        }

        const prevPct = job.lastProgressPercent ?? job.progress?.percent ?? 0;
        const pctChanged = Number(progress.percent) !== Number(prevPct);
        const stuckSince = pctChanged ? Date.now() : (job.progressStuckSince ?? Date.now());
        const stuckMs = Date.now() - stuckSince;

        if (stuckMs >= STALE_PROGRESS_MS && !job.staleNotified) {
          pushNotice({
            variant: "info",
            title: "Descarga SAT en proceso",
            body:
              "El SAT puede tardar varios minutos en preparar cada tramo. La descarga sigue activa en segundo plano.",
          });
          updateJob(job.jobId, {
            status: "running",
            progress: {
              ...progress,
              message:
                progress.message ||
                "El SAT está procesando la solicitud. Puede tardar varios minutos por tramo.",
            },
            statusLine,
            progressStuckSince: stuckSince,
            lastProgressPercent: progress.percent,
            staleNotified: true,
          });
          return false;
        }

        updateJob(job.jobId, {
          status: "running",
          progress,
          statusLine,
          progressStuckSince: stuckSince,
          lastProgressPercent: progress.percent,
        });
        return false;
      } catch (e) {
        updateJob(job.jobId, {
          progress: {
            step: "poll_error",
            message: "Reintentando consulta de estado…",
            percent: job.progress?.percent ?? 0,
          },
        });
        return false;
      }
    },
    [pushNotice, removeJob, updateJob],
  );

  useEffect(() => {
    if (!user?.id) return undefined;
    syncActiveJobs();
    const intervalId = window.setInterval(syncActiveJobs, 60000);
    const onFocus = () => {
      syncActiveJobs();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.id, syncActiveJobs]);

  useEffect(() => {
    const running = jobs.filter((j) => j.status === "running");
    if (!running.length) return undefined;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const active = jobsRef.current.filter((j) => j.status === "running");
      await Promise.all(active.map((j) => pollJobOnce(j)));
    };

    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [jobs.length, pollJobOnce]);

  const activeJobs = useMemo(
    () => jobs.filter((j) => j.status === "running" && !j.staleNotified),
    [jobs],
  );

  const value = useMemo(
    () => ({
      jobs,
      activeJobs,
      activeCount: activeJobs.length,
      notices,
      dismissNotice,
      registerJob,
      syncActiveJobs,
      getJob: (jobId) => jobs.find((j) => j.jobId === jobId),
    }),
    [jobs, activeJobs, notices, dismissNotice, registerJob, syncActiveJobs],
  );

  return (
    <SatDownloadJobsContext.Provider value={value}>{children}</SatDownloadJobsContext.Provider>
  );
}

export function useSatDownloadJobs() {
  const ctx = useContext(SatDownloadJobsContext);
  if (!ctx) {
    throw new Error("useSatDownloadJobs debe usarse dentro de SatDownloadJobsProvider");
  }
  return ctx;
}

/** Hook opcional para paneles fuera del provider (no lanza). */
export function useSatDownloadJobsOptional() {
  return useContext(SatDownloadJobsContext);
}

export { buildCompletionNotice };
